/**
 * REL-8 — the Apple Weather attribution mark. ONE definition, three surfaces.
 *
 * ## The requirement
 *
 * The Apple Developer Program License Agreement and the WeatherKit docs require
 * that **each place weather data is DISPLAYED** shows the Apple Weather mark
 * and links to Apple's attribution page. `apple-attribution-proposal.md`
 * records the founder's ruling — **option A**: a quiet mark per surface where a
 * value renders, linked, nothing in the athlete's face. Three placements:
 * the run summary, the Today hero's meta row, and a line in the chat's ⓘ
 * affordance. **No Live Activity mark** — island space; the run summary carries
 * the pair.
 *
 * The run MAP needs nothing: MapKit draws Apple's own attribution and our only
 * obligation there is never to obscure it.
 *
 * ## Why a component and not three copied strings
 *
 * Because it is a legal string with a legal URL, and three copies drift. It
 * drifting is not a cosmetic bug — a mark that stops linking to the right page
 * stops satisfying the requirement, silently, on whichever surface was missed.
 *
 * ## Why the words and not the  glyph
 *
 * The agreement permits either the ` Weather` lockup or the plain words
 * "Apple Weather"; the proposal quotes both. U+F8FF is a private-use Apple
 * glyph that renders as a replacement box everywhere else, and Tayro ships a
 * web surface as well as the iOS shell. The words are correct on both.
 *
 * ## ⛔ THE GATE IS THE CALLER'S JOB, AND IT IS NOT OPTIONAL
 *
 * Every mark must be dark until weather actually renders. This component does
 * not know whether it does — mount it inside the same guard that renders the
 * value. A mark with no data above it is worse than no mark: it credits Apple
 * for nothing and tells the reader we display weather when we do not.
 */

import styles from './AppleWeatherMark.module.css';

/**
 * Apple's attribution page. The link is half the requirement.
 *
 * ⚠️ B61 — "it takes to a development URL" (founder, Build 26). It does, and
 * the redirect is APPLE'S, not ours. Verified 2026-08-29:
 *
 *   GET https://weatherkit.apple.com/legal-attribution.html
 *     → 308 Permanent Redirect
 *     → https://developer.apple.com/weatherkit/data-source-attribution/
 *
 * That destination IS the attribution page — the list of WeatherKit's
 * meteorological data providers (NOAA, DWD, Met Office, JMA, …). It merely
 * lives on `developer.apple.com`, which reads like an internal/dev link to
 * anybody who did not follow the hop.
 *
 * The URL below is kept as-is, deliberately: it is the URL Apple's own
 * WeatherKit documentation and the Developer Program License Agreement name,
 * so if Apple re-points the destination again we follow automatically. Hard-
 * coding today's `developer.apple.com` target would save one hop and buy a
 * dead link the day they move it.
 */
export const APPLE_WEATHER_LEGAL_URL = 'https://weatherkit.apple.com/legal-attribution.html';

/** The mark's words, exported so tests pin one string rather than three. */
export const APPLE_WEATHER_MARK = 'Apple Weather';

export interface AppleWeatherMarkProps {
  /**
   * `light` — the default, for cream/paper surfaces (Today hero, chat).
   * `dark` — for the run-summary card, which is near-black with white text, so
   * the `--ink-*` scale reads as invisible there.
   */
  tone?: 'light' | 'dark';
  /** Optional leading words, e.g. "Weather by". Omitted → the bare mark. */
  prefix?: string;
}

export function AppleWeatherMark({ tone = 'light', prefix }: AppleWeatherMarkProps) {
  return (
    <span className={styles.mark} data-tone={tone}>
      {prefix ? `${prefix} ` : null}
      <a
        className={styles.link}
        href={APPLE_WEATHER_LEGAL_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {APPLE_WEATHER_MARK}
      </a>
    </span>
  );
}
